import { NativeFormElements } from '../../global/ui-helpers';
import { Optional, UndefinedOr } from '../../global/utils.type';
import { UIElement } from '../ui-element';
import { canExpand } from './ui-field.helpers';
import { runInNextFrame } from '../../global/helpers';

// List of virtual form elements that require special accessibility handling
const MultiInputVirtualFormElements = [
    'ui-monthpicker',
    'ui-monthrange',
    'ui-daterange',
    'ui-timepicker',
    'ui-timerange',
];

/**
 * Updates aria-labels for virtual form components to include the field label context.
 * This ensures screen readers announce the full context for each input within a virtual form control.
 * @param {HTMLElement | null | undefined} control - The form control element
 * @param {string | undefined} fieldLabel - The label text from the parent field
 */
export function updateAriaLabels(
    control: Optional<HTMLElement>,
    fieldLabel: UndefinedOr<string>
): void {
    if (!control || !fieldLabel) {
        return;
    }

    const tagName = control.localName;
    if (!MultiInputVirtualFormElements.includes(tagName)) {
        return;
    }

    const fieldLabelText = fieldLabel.trim();

    if (tagName === 'ui-monthpicker') {
        const monthSelect = control.querySelector<HTMLElement>(
            '.ui-monthpicker__control.-month:not(.-hidden)'
        );
        const yearSelect = control.querySelector<HTMLElement>(
            '.ui-monthpicker__control.-year:not(.-hidden)'
        );

        const monthLabel =
            control
                .querySelector<HTMLLabelElement>('label[for$="-month"]')
                ?.innerText.trim() || 'Month';
        const yearLabel =
            control
                .querySelector<HTMLLabelElement>('label[for$="-year"]')
                ?.innerText.trim() || 'Year';

        if (monthSelect) {
            monthSelect.setAttribute(
                'aria-label',
                `${monthLabel}, ${fieldLabelText}`
            );
        }
        if (yearSelect) {
            yearSelect.setAttribute(
                'aria-label',
                `${yearLabel}, ${fieldLabelText}`
            );
        }
        return;
    }

    const inputs = control.querySelectorAll<HTMLElement>(
        'input:not(.-hidden), select:not(.-hidden)'
    );

    inputs.forEach((input) => {
        const currentLabel = input.getAttribute('aria-label') || '';
        if (!currentLabel.includes(fieldLabelText)) {
            if (
                tagName === 'ui-timerange' ||
                (tagName === 'ui-timepicker' && control.closest('ui-timerange'))
            ) {
                // For timerange, append field label to existing time start/end labels
                const existingLabel = input.getAttribute('aria-label');
                input.setAttribute(
                    'aria-label',
                    `${existingLabel}, ${fieldLabelText}`
                );
            } else if (tagName === 'ui-timepicker') {
                input.setAttribute('aria-label', `Time, ${fieldLabelText}`);
            } else {
                input.setAttribute(
                    'aria-label',
                    currentLabel
                        ? `${currentLabel}, ${fieldLabelText}`
                        : fieldLabelText
                );
            }
        }
    });
}

const interactiveLabelElements = ['ui-tooltip', 'ui-link', 'a'].join(',');

/**
 * Handles click events for form control labels.
 * This includes:
 * 1. Expanding components that need to be expanded before focus
 * 2. Special handling for file upload dialogs
 * 3. Focusing the appropriate input element
 * @param {PointerEvent} e - The click event
 * @param {HTMLElement} control - The form control element
 * @returns {Promise<void>}
 * @memberof SharedComponents
 */
export const handleLabelClick = async (
    e: PointerEvent,
    control: HTMLElement
): Promise<void> => {
    if (!control) {
        return;
    }

    // if target is an interactive element, do not modify click event
    const interactiveTarget = (e.target as HTMLElement).closest(
        interactiveLabelElements
    );
    if (interactiveTarget) {
        return;
    }

    e.preventDefault();

    // For components that need to be expanded first (like dropdowns & datepickers)
    if (canExpand(control)) {
        (control as UIElement & { expanded: boolean }).expanded = true;
        await new Promise<void>((resolve) => {
            const frameId = runInNextFrame(() => resolve());
            if (frameId === -1) {
                resolve();
            }
        });
    }

    // Special handling for upload - triggers native file dialog
    const tagName = control.localName;
    if (tagName === 'ui-upload') {
        const fileInput =
            control.querySelector<HTMLInputElement>('input[type="file"]');
        if (fileInput) {
            fileInput.click();
            return;
        }
    }

    // Find and focus the appropriate input element
    const elementToFocus = findFocusableElement(control);
    if (elementToFocus) {
        elementToFocus.focus();
    } else {
        control.focus();
    }
};

/**
 * Finds the appropriate focusable element within a form control.
 * Each component type may have different elements that should receive focus.
 * @param {HTMLElement} control - The form control element
 * @returns {HTMLElement|null} - The element that should receive focus
 * @private
 */
function findFocusableElement(control: HTMLElement): HTMLElement | null {
    if (!control) {
        return null;
    }

    const tagName = control.localName;
    const ctrl = control as UIElement & { mode?: string };

    switch (tagName) {
        case 'ui-stepper':
            return control.querySelector<HTMLElement>('.ui-stepper__input');

        case 'ui-monthpicker':
            return ctrl.mode === 'input'
                ? control.querySelector<HTMLElement>(
                      'input[type="month"]:not(.-hidden)'
                  )
                : control.querySelector<HTMLElement>(
                      '.ui-monthpicker__control.-month:not(.-hidden)'
                  );

        case 'ui-monthrange': {
            const firstMonthPicker = control.querySelector<
                UIElement & { mode?: string }
            >('ui-monthpicker');
            if (firstMonthPicker) {
                return firstMonthPicker.mode === 'input'
                    ? firstMonthPicker.querySelector<HTMLElement>(
                          'input[type="month"]:not(.-hidden)'
                      )
                    : firstMonthPicker.querySelector<HTMLElement>(
                          '.ui-monthpicker__control.-month:not(.-hidden)'
                      );
            }
            break;
        }

        case 'ui-rating':
            return control.querySelector<HTMLElement>(
                '.ui-rating__radio-group input[type="radio"]'
            );

        default:
            return control.querySelector<HTMLElement>(
                NativeFormElements.map(
                    (selector) =>
                        `${selector}:not(.-visually-hidden):not(.-hidden):not([type=hidden])`
                ).join(',')
            );
    }

    return null;
}
